import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import FormField from '../components/ui/FormField';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './AuditLogs.css';

const ACTION_LABELS = {
  ADMIN_2FA_RESET: '2FA Reset',
  FAILED_2FA_RESET: '2FA Reset Failed',
  INVOICE_PAYMENT_MATCHED: 'Payment Matched',
  INVOICE_PAYMENT_SUGGESTED_MATCH: 'Payment Match — Needs Review',
  IMPERSONATION_START: 'Viewed As Customer',
  IMPERSONATION_END: 'Returned To Admin',
  ITEM_PRICE_UPDATED: 'Item Price Changed',
  HOSTING_ACCOUNT_CREATED: 'Hosting Account Created',
  HOSTING_ACCOUNT_SUSPENDED: 'Hosting Account Suspended',
  HOSTING_ACCOUNT_UNSUSPENDED: 'Hosting Account Unsuspended'
};

const ACTION_TONES = {
  ADMIN_2FA_RESET: 'info',
  FAILED_2FA_RESET: 'danger',
  INVOICE_PAYMENT_MATCHED: 'success',
  INVOICE_PAYMENT_SUGGESTED_MATCH: 'warning',
  IMPERSONATION_START: 'warning',
  IMPERSONATION_END: 'info',
  ITEM_PRICE_UPDATED: 'warning',
  HOSTING_ACCOUNT_CREATED: 'success',
  HOSTING_ACCOUNT_SUSPENDED: 'warning',
  HOSTING_ACCOUNT_UNSUSPENDED: 'info'
};

function formatUser(user) {
  if (!user) return '—';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.email || user.UserName || `#${user.id}`;
}

function AuditLogs() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ action_type: '', from: '', to: '' });

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const fetchLogs = useCallback(async (targetPage, activeFilters) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: targetPage, limit: 25 });
      if (activeFilters.action_type) params.set('action_type', activeFilters.action_type);
      if (activeFilters.from) params.set('from', activeFilters.from);
      if (activeFilters.to) params.set('to', activeFilters.to);

      const data = await apiClient.get(`/api/audit-logs?${params}`);
      setRows(data.rows);
      setPages(data.pages);
      setTotal(data.total);
      setActionTypes(data.actionTypes || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs(1, filters);
  };

  const clearFilters = () => {
    const cleared = { action_type: '', from: '', to: '' };
    setFilters(cleared);
    setPage(1);
    fetchLogs(1, cleared);
  };

  const columns = [
    {
      key: 'timestamp',
      label: 'When',
      sortable: true,
      render: (row) => new Date(row.timestamp).toLocaleString()
    },
    {
      key: 'action_type',
      label: 'Action',
      render: (row) => (
        <span className={`audit-badge audit-badge-${ACTION_TONES[row.action_type] || 'default'}`}>
          {ACTION_LABELS[row.action_type] || row.action_type}
        </span>
      )
    },
    { key: 'adminUser', label: 'By', render: (row) => (row.admin_user_id === 1 ? 'System' : formatUser(row.adminUser)) },
    { key: 'targetUser', label: 'Affected', render: (row) => formatUser(row.targetUser) },
    { key: 'reason', label: 'Reason' },
    {
      key: 'details',
      label: 'Details',
      render: (row) => row.details ? (
        <details className="audit-details">
          <summary>View</summary>
          <pre>{JSON.stringify(row.details, null, 2)}</pre>
        </details>
      ) : '—'
    }
  ];

  return (
    <PageShell>
      <div className="audit-logs-page">
        <h1>Audit Log</h1>
        <p className="audit-logs-intro">Every 2FA reset and automated invoice-payment match, in one place.</p>

        <form className="audit-filters" onSubmit={applyFilters}>
          <FormField id="action_type" label="Action">
            <select
              id="action_type"
              value={filters.action_type}
              onChange={(e) => setFilters((prev) => ({ ...prev, action_type: e.target.value }))}
            >
              <option value="">All actions</option>
              {actionTypes.map((type) => (
                <option key={type} value={type}>{ACTION_LABELS[type] || type}</option>
              ))}
            </select>
          </FormField>
          <FormField id="from" label="From">
            <input
              id="from"
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
            />
          </FormField>
          <FormField id="to" label="To">
            <input
              id="to"
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
            />
          </FormField>
          <div className="audit-filters-actions">
            <Button type="submit">Filter</Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>Clear</Button>
          </div>
        </form>

        {loading ? (
          <p>Loading audit log...</p>
        ) : error ? (
          <div className="audit-logs-error">{error}</div>
        ) : (
          <>
            <ResponsiveTable columns={columns} rows={rows} rowKey="id" emptyMessage="No matching audit log entries." />
            <div className="audit-pagination">
              <Button type="button" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page} of {pages} &middot; {total} total</span>
              <Button type="button" variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}

export default AuditLogs;
